import { useFloatingIconDrag } from 'hooks/useFloatingIconDrag';

/**
 * Phase 6.5 — `#rating_floating-icon`, the MINIMIZED state of the header
 * "Independent Rating" popup.
 *
 * Faithful port of `header/tmpl/rating_dialog_floating_icon.tmpl` (3 lines),
 * injected into `#sizer_popup` and made draggable at header.js:560-570:
 *   if (!$('#sizer_popup #rating_floating-icon')[0]) {
 *     $('#sizer_popup').append(self.floatingIconTemplate());
 *     $('#sizer_popup #rating_floating-icon').draggable();
 *   }
 *   const state = fn.local_storage.get('independent_rating_popup_state');
 *   if (state !== 'minimize') …addClass('hide'); else …removeClass('hide');
 *
 * It is NOT a second surface: its click runs the SAME
 * `openComponentSelectorPopup` as the header button (header.js:57-58), so it is
 * purely the restore-from-minimized affordance — and the ONLY way back from
 * `minimize`.
 *
 * Styling is entirely legacy CSS, already global via react.html:
 * `.floating-icon` (`css/common/sass/ui/popup/ahu_sizer_popup/_sizer.content
 * .scss:348-395`) gives it `position:fixed; top:calc(100% - 65px); right:36px;
 * z-index:100000`, and `.icon-title` shows on hover. `.hide` hides it.
 * (The `+ #rating_floating-icon { right:85px }` sibling shift only applies when
 * the AHU-Sizer popup's own floating icon precedes it — that popup is not
 * ported, so the base offset applies, exactly as on a legacy page with the
 * sizer popup closed.)
 *
 * DRAG: jQuery UI `.draggable()` writes inline `left`/`top` on drag and adds
 * `ui-draggable ui-draggable-handle`. Reproduced with pointer events (no jQuery
 * UI in the React bundle) in `useFloatingIconDrag`, shared with the AHU Sizer
 * icon that sits beside it in `#sizer_popup`; a pointer-up that moved < 3 px
 * still counts as a click, so the restore affordance keeps working.
 */

interface RatingFloatingIconProps {
  /** false → the legacy `hide` class (header.js:566-569). */
  visible: boolean;
  /** header.js:57-58 — the SAME handler as #component_selector_button. */
  onOpen: () => void;
}

export const RatingFloatingIcon = ({ visible, onOpen }: RatingFloatingIconProps) => {
  // a non-drag pointerup IS the click (header.js:57-58).
  const { ref, dragProps } = useFloatingIconDrag<HTMLDivElement>({ onClick: onOpen });

  return (
    <div
      ref={ref}
      id="rating_floating-icon"
      /* `ui-draggable ui-draggable-handle` kept for DOM parity with
         jQuery UI's `.draggable()` (discovery observed `draggable:true`). */
      className={`floating-icon ui-draggable ui-draggable-handle${visible ? '' : ' hide'}`}
      {...dragProps}
    >
      <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 -960 960 960" width="24">
        <path
          d="m482-200 114-113-114-113-42 42 43 43q-28 1-54.5-9T381-381q-20-20-30.5-46T340-479q0-17 4.5-34t12.5-33l-44-44q-17 25-25 53t-8 57q0 38 15 75t44 66q29 29 65 43.5t74 15.5l-38 38 42 42Zm165-170q17-25 25-53t8-57q0-38-14.5-75.5T622-622q-29-29-65.5-43T482-679l38-39-42-42-114 113 114 113 42-42-44-44q27 0 55 10.5t48 30.5q20 20 30.5 46t10.5 52q0 17-4.5 34T603-414l44 44ZM480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Z"
          fill="#fff"
        />
      </svg>
      <span className="icon-title">Component Ratings</span>
    </div>
  );
};

export default RatingFloatingIcon;
